import React, { useState, useEffect, useCallback } from "react";
import RoleBasedSidebar from "components/ui/RoleBasedSidebar";
import Icon from "components/AppIcon";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";


const COLORS = ['#00BCD4', '#008B9A', '#D97706', '#0ABFCC', '#7C3AED', '#059669'];

const KpiCard = ({ title, value, icon, color, subtitle }) => (
  <div className="rounded-xl overflow-hidden border" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', boxShadow: '0 2px 12px rgba(0,188,212,0.08)', borderLeft: '4px solid var(--color-primary)' }}>
    <div className="p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-wide" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}>{title}</span>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
          <Icon name={icon} size={18} color={color} />
        </div>
      </div>
      <div className="text-3xl font-bold" style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)' }}>{value ?? '—'}</div>
      {subtitle && <p className="text-xs mt-1" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}>{subtitle}</p>}
    </div>
  </div>
);

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { userProfile, signOut } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({ totalUsers: 0, activeUsers: 0, pendingUsers: 0, monthlyDownloads: 0 });
  const [downloadsByDay, setDownloadsByDay] = useState([]);
  const [topDownloads, setTopDownloads] = useState([]);
  const [userStatusDist, setUserStatusDist] = useState([]);
  const [userRoleDist, setUserRoleDist] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '', role: '' });

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      // KPIs
      const { data: profiles } = await supabase?.from('profiles')?.select('id, role, status');
      const totalUsers = profiles?.length || 0;
      const activeUsers = profiles?.filter(p => p?.status === 'ACTIVE')?.length || 0;
      const pendingUsers = profiles?.filter(p => p?.status === 'PENDING')?.length || 0;

      // Monthly downloads
      const startOfMonth = new Date();
      startOfMonth?.setDate(1);
      startOfMonth?.setHours(0, 0, 0, 0);
      const { count: monthlyDownloads } = await supabase?.from('download_logs')?.select('*', { count: 'exact', head: true })?.gte('downloaded_at', startOfMonth?.toISOString());

      setKpis({ totalUsers, activeUsers, pendingUsers, monthlyDownloads: monthlyDownloads || 0 });

      // User status distribution
      const statusCounts = {};
      profiles?.forEach(p => { statusCounts[p.status] = (statusCounts?.[p?.status] || 0) + 1; });
      setUserStatusDist(Object.entries(statusCounts)?.map(([name, value]) => ({ name, value })));

      // User role distribution
      const roleCounts = {};
      profiles?.forEach(p => { roleCounts[p.role] = (roleCounts?.[p?.role] || 0) + 1; });
      setUserRoleDist(Object.entries(roleCounts)?.map(([name, value]) => ({ name, value })));

      // Downloads over last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo?.setDate(thirtyDaysAgo?.getDate() - 30);
      const { data: dlLogs } = await supabase?.from('download_logs')?.select('downloaded_at, assignment_id')?.gte('downloaded_at', thirtyDaysAgo?.toISOString())?.order('downloaded_at', { ascending: true });

      // Group by day
      const byDay = {};
      dlLogs?.forEach(log => {
        const day = new Date(log.downloaded_at)?.toLocaleDateString('es-CO', { month: 'short', day: 'numeric' });
        byDay[day] = (byDay?.[day] || 0) + 1;
      });
      setDownloadsByDay(Object.entries(byDay)?.map(([date, downloads]) => ({ date, downloads })));

      // Top downloads by update
      const { data: assignments } = await supabase?.from('update_assignments')?.select('download_count, updates(name, version)')?.order('download_count', { ascending: false })?.limit(10);

      const topMap = {};
      assignments?.forEach(a => {
        const key = `${a?.updates?.name} v${a?.updates?.version}`;
        topMap[key] = (topMap?.[key] || 0) + (a?.download_count || 0);
      });
      setTopDownloads(Object.entries(topMap)?.map(([name, downloads]) => ({ name, downloads }))?.sort((a, b) => b?.downloads - a?.downloads)?.slice(0, 10));

      // Recent audit logs
      const { data: auditData } = await supabase?.from('audit_logs')?.select('*, profiles(full_name, email)')?.order('created_at', { ascending: false })?.limit(50);
      setRecentActivity(auditData || []);

    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const currentUser = {
    name: userProfile?.full_name || 'Administrador',
    role: userProfile?.role || 'ADMIN',
    organization: 'Update-vital-Ftech',
  };

  const ACTION_LABELS = {
    USER_ACTIVATED: 'Usuario activado',
    USER_REJECTED: 'Usuario rechazado',
    USER_SUSPENDED: 'Usuario suspendido',
    USER_REGISTERED: 'Nuevo registro',
    UPDATE_ASSIGNED: 'Actualización asignada',
    FILE_DOWNLOADED: 'Archivo descargado',
  };

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: "var(--color-background)" }}>
      <RoleBasedSidebar
        user={currentUser}
        notifications={{ pendingApprovals: kpis?.pendingUsers }}
        isCollapsed={sidebarCollapsed}
        onCollapsedChange={setSidebarCollapsed}
        onLogout={handleLogout}
      />
      <main className="flex-1 overflow-y-auto">
        {/* Header — Vital branded */}
        <div className="sticky top-0 z-10 px-6 py-4 border-b flex items-center justify-between" style={{ background: "var(--color-card)", borderColor: "var(--color-primary)", borderBottomWidth: '2px', boxShadow: '0 2px 8px rgba(0,188,212,0.1)' }}>
          <div className="flex items-center gap-3 pl-10 lg:pl-0">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#00BCD4" }}>
              <Icon name="BarChart3" size={18} color="#fff" />
            </div>
            <div>
              <h1 className="text-heading-4 font-heading font-bold leading-tight" style={{ color: "var(--color-foreground)" }}>Analíticas</h1>
              <p className="text-xs font-caption" style={{ color: "var(--color-primary)" }}>Métricas de la plataforma Update-vital-Ftech</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchAnalytics} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-caption" style={{ borderColor: 'var(--color-primary)', color: 'var(--color-primary)', backgroundColor: 'var(--color-card)' }}>
              <Icon name="RefreshCw" size={13} color="currentColor" />
              Actualizar
            </button>
            <span className="text-xs font-caption px-2.5 py-1 rounded-full" style={{ background: "#B2EBF2", color: "#008B9A" }}>
              <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ backgroundColor: '#00BCD4' }} />
              Datos en tiempo real
            </span>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 0.8s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
            </div>
          ) : (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard title="Total Usuarios" value={kpis?.totalUsers} icon="Users" color="#00BCD4" subtitle="Registrados en el sistema" />
                <KpiCard title="Usuarios Activos" value={kpis?.activeUsers} icon="UserCheck" color="#008B9A" subtitle="Con acceso habilitado" />
                <KpiCard title="Pendientes" value={kpis?.pendingUsers} icon="Clock" color="#D97706" subtitle="Esperando activación" />
                <KpiCard title="Descargas del Mes" value={kpis?.monthlyDownloads} icon="Download" color="#0ABFCC" subtitle="Este mes" />
              </div>

              {/* Downloads over time */}
              <div className="rounded-xl p-5 border" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', borderLeft: '4px solid var(--color-primary)', boxShadow: '0 2px 12px rgba(0,188,212,0.08)' }}>
                <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)' }}>Descargas últimos 30 días</h3>
                {downloadsByDay?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={downloadsByDay}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: 'var(--font-caption)', fill: 'var(--color-muted-foreground)' }} />
                      <YAxis tick={{ fontSize: 11, fontFamily: 'var(--font-caption)', fill: 'var(--color-muted-foreground)' }} />
                      <Tooltip contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '8px', fontFamily: 'var(--font-caption)', fontSize: '12px' }} />
                      <Line type="monotone" dataKey="downloads" stroke="#00BCD4" strokeWidth={2.5} dot={false} name="Descargas" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-40 text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Sin datos de descargas en los últimos 30 días</div>
                )}
              </div>

              {/* Top downloads + Status distribution */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Top downloads bar chart */}
                <div className="rounded-xl p-5 border" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', borderLeft: '4px solid var(--color-primary)', boxShadow: '0 2px 12px rgba(0,188,212,0.08)' }}>
                  <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)' }}>Top 10 Actualizaciones Más Descargadas</h3>
                  {topDownloads?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={topDownloads} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                        <XAxis type="number" tick={{ fontSize: 10, fontFamily: 'var(--font-caption)', fill: 'var(--color-muted-foreground)' }} />
                        <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 9, fontFamily: 'var(--font-caption)', fill: 'var(--color-muted-foreground)' }} />
                        <Tooltip contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '8px', fontFamily: 'var(--font-caption)', fontSize: '12px' }} />
                        <Bar dataKey="downloads" fill="#00BCD4" name="Descargas" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-40 text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Sin datos de descargas</div>
                  )}
                </div>

                {/* User status pie + role donut */}
                <div className="space-y-4">
                  <div className="rounded-xl p-5 border" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', borderLeft: '4px solid var(--color-primary)', boxShadow: '0 2px 12px rgba(0,188,212,0.08)' }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)' }}>Distribución por Estado</h3>
                    {userStatusDist?.length > 0 ? (
                      <div className="flex items-center gap-4">
                        <ResponsiveContainer width={120} height={120}>
                          <PieChart>
                            <Pie data={userStatusDist} cx="50%" cy="50%" innerRadius={30} outerRadius={55} dataKey="value">
                              {userStatusDist?.map((entry, index) => <Cell key={index} fill={COLORS?.[index % COLORS?.length]} />)}
                            </Pie>
                            <Tooltip contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '8px', fontFamily: 'var(--font-caption)', fontSize: '11px' }} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="space-y-1.5">
                          {userStatusDist?.map((entry, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS?.[i % COLORS?.length] }} />
                              <span className="text-xs" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}>{entry?.name}: <strong>{entry?.value}</strong></span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-center py-4" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Sin datos</div>
                    )}
                  </div>

                  <div className="rounded-xl p-5 border" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', borderLeft: '4px solid var(--color-primary)', boxShadow: '0 2px 12px rgba(0,188,212,0.08)' }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)' }}>Usuarios por Rol</h3>
                    {userRoleDist?.length > 0 ? (
                      <div className="flex items-center gap-4">
                        <ResponsiveContainer width={120} height={120}>
                          <PieChart>
                            <Pie data={userRoleDist} cx="50%" cy="50%" outerRadius={55} dataKey="value">
                              {userRoleDist?.map((entry, index) => <Cell key={index} fill={COLORS?.[(index + 2) % COLORS?.length]} />)}
                            </Pie>
                            <Tooltip contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '8px', fontFamily: 'var(--font-caption)', fontSize: '11px' }} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="space-y-1.5">
                          {userRoleDist?.map((entry, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS?.[(i + 2) % COLORS?.length] }} />
                              <span className="text-xs" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}>{entry?.name}: <strong>{entry?.value}</strong></span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-center py-4" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Sin datos</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Recent Activity Table */}
              <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', boxShadow: '0 2px 12px rgba(0,188,212,0.08)' }}>
                <div className="px-5 py-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
                  <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)' }}>Actividad Reciente (últimas 50 acciones)</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm vital-table">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-left">Acción</th>
                        <th className="px-4 py-3 text-left">Actor</th>
                        <th className="px-4 py-3 text-left">Fecha</th>
                        <th className="px-4 py-3 text-left">Detalles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentActivity?.length === 0 ? (
                        <tr><td colSpan={4} className="px-4 py-8 text-center text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Sin actividad reciente</td></tr>
                      ) : (
                        recentActivity?.map((log, i) => (
                          <tr key={log?.id} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(0,188,212,0.12)', color: 'var(--color-primary)', fontFamily: 'var(--font-caption)' }}>
                                {ACTION_LABELS?.[log?.action] || log?.action}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}>{log?.profiles?.full_name || log?.profiles?.email || '—'}</td>
                            <td className="px-4 py-3 text-xs" style={{ fontFamily: 'var(--font-data)', color: 'var(--color-muted-foreground)' }}>{new Date(log.created_at)?.toLocaleString('es-CO')}</td>
                            <td className="px-4 py-3 text-xs max-w-xs truncate" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}>{log?.metadata ? JSON.stringify(log?.metadata)?.slice(0, 60) : '—'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AnalyticsDashboard;
